// pusherController.js
'use strict';
import { LRUCache } from 'lru-cache';

import { getSentry, getSentry2Scope } from '../resource/sentry.js';
import {
  ON_AUTHENTICATED,
  CHANNEL_SUBSCRIPTION_UPDATED,
  SET_PUSHER_CONNECTION_STATUS,
  SERVICE_WORKER_PONG,
  LOGOUT_FROM_SW,
  PUSHER_EVENT_GOT,
  RESTORE_REDUX,
} from './SWMessageTypes.js';

import {
  SUBSCRIPTION_SUCCEEDED,
  SUBSCRIPTION_ERROR,
  EVENTS,
  CONFIGURATION_UPDATED,
  GIFT_SENT,
  CHAT_USER_ENTERED,
  CHAT_MESSAGE_SENT,
  SIC_BO_CREATED,
  SIC_BO_ENDED,
  SESSION_RATED,
  LIVESTREAM_CHAT_UNPINNED,
} from './PusherEvents.js';

import { CONFIG_PRIORITY_PRESENCE_USER } from '../resource/configPriority.js';
import {
  STREAM_RELATED_EVENTS,
  getIsUserBoundChannel,
} from '../resource/pusherUtils.js';

import Pusher from './pusher.js';
import { decrypt } from './pusherKeychain.js';

import { getIsInServiceWorker } from '../resource/getJsEnvironment.js';
import { pusher as pusherDebug } from '../resource/debug.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';
import batchPusherEvents from '../resource/batchPusherEvents.js';

const pusherLog = pusherDebug.extend('log:pusherController');
const pusherErrorLog = pusherDebug.extend('error:pusherController');

const isOnServiceWorker = getIsInServiceWorker();

let reaperTimer;
const pendingChannels = new Set();
const resubscribedPendingChannels = new LRUCache({ max: 32 });

// Fallback for sendMessageToApp, set from outside (main app only).
// ⚠️ Don't load it here with `await import(...)`: a dynamic import in this
// SW file makes the SW reinstall on every deploy.
let sendMessageToAppFallback = null;

/**
 * Sets the fallback above. The main app calls this once at startup; the SW
 * never does (so it stays null in the SW — fine, the SW skips that path).
 * @param {function} fallback - `sendMessageToApp_fallback` from `./helpers.js`.
 * @example
 * setSendMessageToAppFallback(sendMessageToApp_fallback);
 */
export const setSendMessageToAppFallback = fallback => {
  sendMessageToAppFallback = fallback;
};

/**
 * Send message from Service Worker to App
 * If service worker is not activated, tasks will fallback onto window context automatically
 * @param {object} event
 */
export const sendMessageToApp = async event => {
  if (!isOnServiceWorker) {
    if (!sendMessageToAppFallback) {
      // Shouldn't happen: helpers.js sets the fallback at startup. If it's
      // still null, log it instead of silently dropping the event (which
      // would lose a Pusher message with no trace).
      pusherErrorLog('sendMessageToApp: fallback not injected', { event });
      return;
    }
    return sendMessageToAppFallback(event);
  }

  const clients = await self.clients.matchAll();
  clients.forEach(client => {
    client.postMessage(event);
  });
};

/**
 * Send message from Service Worker to App
 * If service worker is not activated, tasks will fallback onto window context automatically
 * @param {object} event
 * @param {object} tabId - specific browser tab id or other client id
 */
export const logoutOtherTabs = async (event, tabId) => {
  if (!isOnServiceWorker) return;
  const clients = await self.clients.matchAll();
  clients
    .filter(client => client.id !== tabId)
    .forEach(client => {
      client.postMessage(event);
    });
};

const decryptPusherPayload = ({ channelName, payload }) => {
  if (!payload?.nonce) {
    return payload;
  }

  try {
    const decryptedPayload = JSON.parse(
      decrypt({
        nonce: payload.nonce,
        cipherText: payload.ciphertext,
        channelName,
      })
    );

    return decryptedPayload;
  } catch (error) {
    pusherErrorLog(error);
    getSentry().then(({ withScope, captureException }) => {
      if (withScope && captureException) {
        withScope(scope => {
          scope.setExtra('channelName', channelName);
          scope.setExtra('payload', payload);
          captureException(error);
        });
      }
    });
  }
};

/**
 * @param {import('pusher-js').Channel} channel
 * @param {object|function} override metadata to be sent with
 */
export const bindEncryptedEvents = (channel, override = {}) => {
  channel.unbind(EVENTS); // Prevent rebinding
  channel.bind(EVENTS, _events => {
    const events = decryptPusherPayload({
      channelName: channel.name,
      payload: _events,
    });
    pusherLog(channel.name, events);

    const batchedEvents = batchPusherEvents({
      types: [
        CHAT_USER_ENTERED,
        CHAT_MESSAGE_SENT,
        GIFT_SENT,
        SIC_BO_CREATED,
        SIC_BO_ENDED,
        SESSION_RATED,
        LIVESTREAM_CHAT_UNPINNED,
      ],
      events: events,
    });
    batchedEvents?.forEach(({ event, data = {} } = {}) => {
      sendMessageToApp({
        type: PUSHER_EVENT_GOT,
        payload: {
          channelName: channel.name,
          eventType: event,
          payload: data,
        },
      });
      return sendMessageToApp({
        type: event,
        payload: {
          ...data,
          ...(typeof override === 'function' ? override(event) : override),
        },
      });
    });
  });
  if (channel?.callbacks?._callbacks?.[`_${EVENTS}`]) {
    // Prevent unbind race condition.
    channel.callbacks._callbacks[`_${EVENTS}`] = [
      channel.callbacks._callbacks[`_${EVENTS}`][0],
    ];
  }
};

/**
 * @param {import('pusher-js').Channel} channel
 */
export const bindSubscriptionEvents = channel => {
  channel.unbind(SUBSCRIPTION_ERROR);
  channel.bind(SUBSCRIPTION_ERROR, () => {
    unsubscribeChannel(channel.name);
  });
  channel.unbind(SUBSCRIPTION_SUCCEEDED);
  channel.bind(SUBSCRIPTION_SUCCEEDED, () => {
    sendMessageToApp({
      type: CHANNEL_SUBSCRIPTION_UPDATED,
      payload: {
        [channel.name]: {
          subscribed: channel.subscribed,
          subscriptionCancelled: channel.subscriptionCancelled,
          subscriptionPending: channel.subscriptionPending,
        },
      },
    });
  });
};

/**
 * Unsubscribe then subscribe the channel
 * When the channel subscription is stuck on the pending status,
 * unsubscribe and then subscribe does not relieve the situation
 * After try and error, we need to do the following steps
 * unsubscribe -> disconnect -> delete channel -> subscribe
 * Then the channel subscription backs normal
 * @param {String} channelName
 */
const subscribeChannelSafely = async ({ channelName, shouldRetry }) => {
  const socket = Pusher.getSocket();
  const channel = socket?.channels?.channels?.[channelName];

  if (channel) {
    channel?.unsubscribe();
    channel?.disconnect();
    delete socket.channels.channels[channelName];
  }

  return Pusher.subscribe({ channelName, shouldRetry });
};

const unsubscribeChannel = channelName => {
  Pusher.unsubscribe(channelName);
  sendMessageToApp({
    type: CHANNEL_SUBSCRIPTION_UPDATED,
    payload: {
      [channelName]: undefined,
    },
  });
};

/**
 * subscribe private user v2 channel
 * @param {string} {event.payload.userId} - user id.
 */
export const subscribeToPrivateUserV2Channel = async (event = {}) => {
  const { userId } = event.payload || {};
  if (!userId) {
    return;
  }
  const channel = await subscribeChannelSafely({
    channelName: `private-enc-user-v2@${userId}`,
  });
  bindSubscriptionEvents(channel);
  bindEncryptedEvents(channel, event =>
    event === CONFIGURATION_UPDATED
      ? { priority: CONFIG_PRIORITY_PRESENCE_USER }
      : {}
  );
};

const pendingChannelReaper = ({ reaperInterval = 2000 } = {}) => {
  if (reaperTimer) clearInterval(reaperTimer);
  reaperTimer = setInterval(() => {
    const socket = Pusher.getSocket();

    if (socket) {
      socket
        .allChannels()
        .filter(channel => channel.subscriptionPending)
        .forEach(channel => {
          const channelName = channel.name;
          const isSubscriptionPending = channel.subscriptionPending;

          if (pendingChannels.has(channelName)) {
            if (isSubscriptionPending) {
              pusherLog('pending channel', channelName, {
                subscribed: channel.subscribed,
                subscriptionCancelled: channel.subscriptionCancelled,
                subscriptionPending: channel.subscriptionPending,
              });
              const isSubscriptionCancelled = channel.subscriptionCancelled;

              // remove zombie channels
              channel.unsubscribe();
              channel.disconnect();
              delete socket.channels.channels[channelName];

              // only retry once, otherwise we may potentially attack backend
              if (
                !isSubscriptionCancelled &&
                !resubscribedPendingChannels.has(channelName)
              ) {
                Pusher.subscribe({ channelName });
                resubscribedPendingChannels.set(channelName, 1);
                pusherLog('resubscribe', channelName, {
                  pendingChannelsSize: pendingChannels.size,
                  resubscribedPendingChannelsSize:
                    resubscribedPendingChannels.size,
                });
              }
            } else {
              pendingChannels.delete(channelName);

              if (resubscribedPendingChannels.has(channelName))
                resubscribedPendingChannels.delete(channelName);
            }
          } else {
            pendingChannels.add(channelName);
          }
        });

      // clear the channels that non-exist or subscribe success
      const remainingAllChannels = socket.channels.channels;
      pendingChannels.forEach(channelName => {
        if (
          !remainingAllChannels[channelName] ||
          remainingAllChannels[channelName]?.subscribed
        )
          pendingChannels.delete(channelName);
      });

      resubscribedPendingChannels.forEach((_, channelName) => {
        if (
          !remainingAllChannels[channelName] ||
          remainingAllChannels[channelName]?.subscribed
        )
          resubscribedPendingChannels.delete(channelName);
      });

      pusherLog(
        'pending and retried channel status',
        {
          pendingChannelsSize: pendingChannels.size,
          resubscribedPendingChannelsSize: resubscribedPendingChannels.size,
        },
        {
          pendingChannels,
          resubscribedPendingChannels,
        }
      );
    }
  }, reaperInterval);
};

/**
 * connect pusher and subscribe to channels
 */
export const init = event => {
  const { pusherConfig = {} } = event.payload;
  const clientId = pusherConfig.headers?.['X-Client-ID'];
  getSentry().then(({ setTag }) => {
    if (setTag) {
      setTag('client_id', clientId);
    }
  });

  if (pusherConfig.resourceUrl) {
    globalThis.__RESOURCE_URL__ = pusherConfig.resourceUrl;
  }

  Pusher.connect({
    ...pusherConfig,
    onAuth: ({ channel, data }) => {
      sendMessageToApp({
        type: ON_AUTHENTICATED,
        payload: { channel, ...data },
      });
    },
    onEvent: event => {
      const { name, state } = event;
      if ('state_change' === name) {
        sendMessageToApp({
          type: SET_PUSHER_CONNECTION_STATUS,
          payload: { state, current: state },
        });
      }
    },
  });
  Pusher.connectEventListener(({ current, previous }) =>
    sendMessageToApp({
      type: SET_PUSHER_CONNECTION_STATUS,
      payload: {
        state: current,
        current,
        previous,
      },
    })
  );

  pendingChannelReaper({ reaperInterval: pusherConfig.reaperInterval });
};

export const disconnect = () => {
  Pusher.disconnect();
};

/**
 * Get the A/B test token
 * @param {Object} event
 * @param {{ userId: string }} event.payload
 */
export const getAbTestToken = ({ payload }) => {
  const { userId } = payload;
  subscribeToPrivateUserV2Channel({
    payload: {
      userId,
    },
  });
};

/**
 * login
 * @param {Object} event - the event object from the client
 */
export const login = async event => {
  const Sentry = await getSentry();
  const { userId, headers } = event.payload;
  const sentry2Scope = getSentry2Scope();
  if (Sentry) {
    const scope = Sentry.getCurrentScope();
    scope.setUser({ id: userId });
  }

  if (sentry2Scope) sentry2Scope.setUser({ id: userId });

  Pusher.setAuthHeaders({ headers });
  subscribeToPrivateUserV2Channel({
    payload: {
      userId,
    },
  });

  const meStatus = {
    payload: {
      userId,
      shouldRetry: false,
    },
  };
  userOnlineStatus(meStatus);
};

/**
 * setAuthHeaders
 * @param {Object} headers - the event object from the client
 */
export const setAuthHeaders = event => {
  const { headers } = event.payload;
  Pusher.setAuthHeaders({ headers });
};

export const setAuthorizerLanguage = async event => {
  const { language } = event.payload;
  await Pusher.setAuthorizerLanguage({ language });
};

/**
 * Unsubscribe channels bound to user identity (see getIsUserBoundChannel)
 */
export const logout = (event, tabId) => {
  Pusher.getAllChannels()?.forEach(({ name }) => {
    if (getIsUserBoundChannel({ channelName: name })) {
      unsubscribeChannel(name);
    }
  });
  logoutOtherTabs({ type: LOGOUT_FROM_SW }, tabId);
};

/**
 * check user online status
 * @param {function} {event.dispatch}
 * @param {object} {event.payload}
 * @param {string} {event.payload.userId}
 */
export const userOnlineStatus = async event => {
  const { userId, shouldRetry } = event.payload;
  const channel = await subscribeChannelSafely({
    channelName: `private-enc-user@${userId}`,
    shouldRetry,
  });
  bindSubscriptionEvents(channel);
  bindEncryptedEvents(channel, event =>
    STREAM_RELATED_EVENTS.includes(event)
      ? { userId, streamId: userId }
      : { userId }
  );
};

export const subscribePresenceMessageChannel = async event => {
  const { messageId, uploadJobId } = event.payload;
  const channel = await subscribeChannelSafely({
    channelName: `presence-enc-message@${messageId}`,
  });
  bindSubscriptionEvents(channel);
  bindEncryptedEvents(channel, { messageId, uploadJobId });
};

export const unsubscribePresenceMessageChannel = event => {
  const { messageId } = event.payload;
  unsubscribeChannel(`presence-enc-message@${messageId}`);
};

/**
 * Ping Service Worker
 */
export const pingServiceWorker = event => {
  const { timestamp } = event.payload;

  const connection = Pusher.getConnectState();
  sendMessageToApp({
    type: SET_PUSHER_CONNECTION_STATUS,
    payload: {
      state: connection,
    },
  });

  if (
    !connection ||
    ['unavailable', 'failed', 'disconnected'].includes(connection)
  ) {
    sendMessageToApp({
      type: SERVICE_WORKER_PONG,
      payload: {
        status: {
          error: 'pusher-connection-failed',
          connection,
        },
      },
    });
  } else {
    sendMessageToApp({
      type: SERVICE_WORKER_PONG,
      payload: {
        status: {
          pong: timestamp,
        },
      },
    });
  }
};

/**
 * Get pusher connection Status
 */
export const getPusherConnectionStatus = () => {
  const name = Pusher.getSocket()?.connection?.connection?.transport.name;
  sendMessageToApp({
    type: SET_PUSHER_CONNECTION_STATUS,
    payload: {
      transport: {
        name,
      },
    },
  });
};

/**
 * subscribe private stream channel
 * @param {string} {event.payload.streamId} - stream id.
 */
export const subscribeStreamChannel = async event => {
  const { streamId } = event.payload;
  const channel = await subscribeChannelSafely({
    channelName: `private-enc-stream@${streamId}`,
  });
  bindSubscriptionEvents(channel);
  bindEncryptedEvents(channel, {
    streamId,
  });
};

/**
 * unsubscribe private stream channel
 * @param {string} {event.payload.streamId} - stream id.
 */
export const unsubscribeStreamChannel = event => {
  const { streamId } = event.payload;
  unsubscribeChannel(`private-enc-stream@${streamId}`);
};

/**
 * subscribe private goal channel
 * @param {string} {event.payload.goalId} - goal id.
 */
export const subscribeGoalChannel = async event => {
  const { goalId } = event.payload;
  const channel = await subscribeChannelSafely({
    channelName: `private-enc-goal@${goalId}`,
  });
  bindSubscriptionEvents(channel);
  bindEncryptedEvents(channel, { goalId });
};

/**
 * unsubscribe private goal channel
 * @param {string} {event.payload.goalId} - goal id.
 */
export const unsubscribeGoalChannel = event => {
  const { goalId } = event.payload;
  unsubscribeChannel(`private-enc-goal@${goalId}`);
};

/**
 * subscribe private goal v2 channel
 * @param {string} {event.payload.goalId} - goal id.
 * @param {string} {event.payload.userId} - user id.
 */
export const subscribePrivateGoalV2Channel = async event => {
  const { goalId, userId } = event.payload;
  const channel = await subscribeChannelSafely({
    channelName: `private-enc-goal-v2@${goalId}.${userId}`,
  });
  bindSubscriptionEvents(channel);
  bindEncryptedEvents(channel, { goalId });
};

/**
 * unsubscribe private goal v2 channel
 * @param {string} {event.payload.goalId} - goal id.
 * @param {string} {event.payload.userId} - user id.
 */
export const unsubscribePrivateGoalV2Channel = event => {
  const { goalId, userId } = event.payload;
  unsubscribeChannel(`private-enc-goal-v2@${goalId}.${userId}`);
};

/**
 * subscribe campaign channel
 * @param {string} {event.payload.campaignName} - campaign name.
 */
export const subscribeCampaignChannel = async event => {
  const { campaignName } = event.payload;
  const channel = await subscribeChannelSafely({
    channelName: `private-enc-campaign@${campaignName}`,
  });
  bindSubscriptionEvents(channel);
  bindEncryptedEvents(channel, { campaignName });
};

/**
 * unsubscribe campaign channel
 * @param {string} {event.payload.campaignName} - campaign name.
 */
export const unsubscribeCampaignChannel = event => {
  const { campaignName } = event.payload;
  unsubscribeChannel(`private-enc-campaign@${campaignName}`);
};

/**
 * subscribe feed channel
 * @param {string} {event.payload.feedName} - feed name.
 */
export const subscribeFeedChannel = async event => {
  const { feedName } = event.payload;
  const channel = await subscribeChannelSafely({
    channelName: `private-enc-feed@${feedName}`,
  });
  bindSubscriptionEvents(channel);
  bindEncryptedEvents(channel, { feedName });
};

/**
 * unsubscribe feed channel
 * @param {string} {event.payload.feedName} - feed name.
 */
export const unsubscribeFeedChannel = event => {
  const { feedName } = event.payload;
  unsubscribeChannel(`private-enc-feed@${feedName}`);
};

/**
 * Subscribe presence asset channel
 * @param {string} {event.payload.assetId} - asset id.
 * @param {boolean} {event.payload.shouldRetry} - should retry subscribe channel.
 */
export const subscribeAssetChannel = async event => {
  const { assetId, shouldRetry = true } = event.payload;
  const channel = await subscribeChannelSafely({
    channelName: `presence-enc-asset@${assetId}`,
    shouldRetry,
  });
  bindSubscriptionEvents(channel);
  bindEncryptedEvents(channel, { assetId });
};

/**
 * Unsubscribe presence asset channel
 * @param {string} {event.payload.assetId} - asset id.
 */
export const unsubscribeAssetChannel = event => {
  const { assetId } = event.payload;
  unsubscribeChannel(`presence-enc-asset@${assetId}`);
};

/**
 * Subscribe presence order channel
 * @param {string} {event.payload.orderId} - order id.
 */
export const subscribeOrderChannel = async event => {
  const { orderId } = event.payload;
  const channel = await subscribeChannelSafely({
    channelName: `presence-enc-order@${orderId}`,
  });
  bindSubscriptionEvents(channel);
  bindEncryptedEvents(channel, { orderId });
};

/**
 * Subscribe presence stream exclusive channel
 * @param {string} {event.payload.streamId} - livestream id.
 */
export const subscribePresenceStreamExclusive = async event => {
  const { streamId } = event.payload;
  const channel = await subscribeChannelSafely({
    channelName: `presence-enc-stream-exclusive@${streamId}`,
  });
  bindSubscriptionEvents(channel);
  bindEncryptedEvents(channel, { streamId });
};

/**
 * Unsubscribe presence stream exclusive channel
 * @param {string} {event.payload.streamId} - livestream id.
 */
export const unsubscribePresenceStreamExclusive = event => {
  const { streamId } = event.payload;
  unsubscribeChannel(`presence-enc-stream-exclusive@${streamId}`);
};

/**
 * Get Channel Status
 * @param {string} {event.payload.channelNames} - channel name list.
 */
export const getChannelStatus = event => {
  const { channelNames = [] } = event.payload;
  const result = objectifyArrayById({
    array: channelNames.map(channelName => {
      const channel =
        Pusher.getSocket()?.channels?.channels?.[channelName] || {};
      const { subscribed, subscriptionCancelled, subscriptionPending } =
        channel;
      return {
        subscribed,
        subscriptionCancelled,
        subscriptionPending,
        channelName,
      };
    }),
    keyAsId: 'channelName',
  });
  sendMessageToApp({
    type: CHANNEL_SUBSCRIPTION_UPDATED,
    payload: result,
  });
};

/**
 * Restore redux from indexedDB
 * @param {string} tabId - specific browser tab id or other client id
 */
export const restoreRedux = async tabId => {
  if (!isOnServiceWorker) return;
  const clients = await self.clients.matchAll();

  clients
    .filter(client => client.id !== tabId)
    .forEach(client => {
      client.postMessage({ type: RESTORE_REDUX });
    });
};
